import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { useContext, useMemo, useState } from 'react';
import { ReorderUserRuleContext } from '../../../../../../context/ReorderUserRuleContext';
import React from 'react';
import { ReorderUserRule, ReorderUserRuleEntityType } from '../../../../../../types/reorderUserRule';
import { ProductMasterData } from '../../../../../../types/product_master_data/productMasterData';
import { StockLocation } from '../../../../../../types/stock_location/stockLocation';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { t } from '../../../../../../types/translation/Translator';
import DeleteReorderUserRuleModal from './Modals/DeleteReorderUserRuleModal';
import { CompanyRole } from '../../../../../../types/company_role_assignment/companyRoleAssignment';
import UpsertReorderUserRuleModal from './Modals/UpsertReorderUserRuleModal';
import { StockLocationRoleAssignmentContext } from '../../../../../../context/StockLocationRoleAssignmentContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { CompanyRoleAssignmentContext } from '../../../../../../context/CompanyRoleAssignmentContext';
import SearchBarWithFilter from '../../../../../../VentoryUI/components/common/SearchBarWithFilter';
import { ReorderUserRuleFilter } from '../../../../../../VentoryUI/components/filters/ReorderRuleFilter';
import Table, { TableHeader } from '../../../../../components/Table';
import { CompanyRoleAssignmentUtil } from '../../../../../../types/company_role_assignment/companyRoleAssignment.util';
import {
  StockLocationRoleAssignmentUtil,
  StockLocationRole,
} from '../../../../../../types/stock_location_role_assignment';
import { CreateButton, DeleteButton } from '../../../../../components/Button';

const getHeaders = (
  productMasterData: Map<string, ProductMasterData>,
  stockLocations: Map<string, StockLocation>,
): TableHeader<ReorderUserRuleGroup>[] => [
  {
    key: 'name',
    name: 'Name',
    text: item => item.rules[0].name,
  },
  {
    key: 'products',
    name: 'Products',
    text: (item: ReorderUserRuleGroup) => {
      if (!item.rules[0].productMasterDataId) return 'Any product';
      if (item.rules.length === 1) {
        return productMasterData.get(item.rules[0].productMasterDataId!)?.productName || 'Unknown Product';
      }
      return new Set(item.rules.map(rule => rule.productMasterDataId)).size.toString();
    },
  },
  {
    key: 'stockLocation',
    name: 'Stock Location',
    text: (item: ReorderUserRuleGroup) => {
      if (!item.rules[0].stockLocationId) return 'Any location';

      if (item.rules.length === 1) {
        return `In ${stockLocations.get(item.rules[0].stockLocationId)?.name || 'Unknown Location'}`;
      }

      return new Set(item.rules.map(rule => rule.stockLocationId)).size.toString();
    },
  },

  {
    key: 'replenishOrderStockLocationRoles',
    name: 'Roles for replenishment',
    text: (item: ReorderUserRuleGroup) => {
      if (
        item.rules[0].replenishOrderCompanyRoles.length === 1 &&
        item.rules[0].replenishOrderCompanyRoles[0] === CompanyRole.administrator
      ) {
        return CompanyRoleAssignmentUtil.roleToString(item.rules[0].replenishOrderCompanyRoles[0]);
      } else if (item.rules[0].replenishOrderStockLocationRoles.length === 1) {
        return StockLocationRoleAssignmentUtil.roleToString(item.rules[0].replenishOrderStockLocationRoles[0]);
      } else {
        return item.rules[0].replenishOrderStockLocationRoles.length.toString();
      }
    },
  },
  {
    key: 'replenishOrderUsers',
    name: 'Users for replenishment',
    text: (item: ReorderUserRuleGroup) => {
      return item.rules[0].replenishOrderUserIds.length.toString();
    },
  },
];

export interface ReorderUserRuleGroup {
  groupId: string;
  rules: ReorderUserRule[];
}

export default function OrderReorderUsersPane() {
  const { groupedReorderUserRules, reorderUserRulesLoading } = useContext(ReorderUserRuleContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { currentCompany } = useContext(CompanyContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);
  const { hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);

  const [upsertModalProps, setUpsertModalProps] = useState<{ groupId?: string; open: boolean }>({
    groupId: undefined,
    open: false,
  });
  function setUpsertModalOpen(open: boolean) {
    setUpsertModalProps({ ...upsertModalProps, open: open });
  }
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const filteredRules = useMemo(() => {
    const groups: ReorderUserRuleGroup[] = [];

    for (const [groupId, rules] of groupedReorderUserRules) {
      if (
        rules[0].entityType === ReorderUserRuleEntityType.productMasterData &&
        (hasCompanyRole(currentCompany.id, CompanyRole.administrator) ||
          !rules.some(
            rule =>
              rule.stockLocationId &&
              !hasStockLocationRole(rule.companyId, rule.stockLocationId, StockLocationRole.STOCK_LOCATION_MANAGER),
          ))
      ) {
        groups.push({ groupId, rules });
      }
    }

    return groups;
  }, [groupedReorderUserRules, reorderUserRulesLoading]);

  const [items, setItems] = useState(filteredRules);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filter = new ReorderUserRuleFilter();

  return (
    <>
      <UpsertReorderUserRuleModal
        open={upsertModalProps.open}
        setOpen={setUpsertModalOpen}
        groupId={upsertModalProps.groupId}
      />
      <DeleteReorderUserRuleModal open={deleteModalOpen} setOpen={setDeleteModalOpen} groupIds={[...selected]} />

      <div className='content-x flex h-full flex-col gap-4'>
        <div className='flex justify-between gap-4'>
          <SearchBarWithFilter
            items={filteredRules}
            setItems={setItems}
            placeholder={t(TranslationKey.filterReorderRules).singular.label}
            filter={filter}
          />
          <div className='flex gap-2'>
            {selected.size ? <DeleteButton onClick={() => setDeleteModalOpen(true)} /> : null}
            <CreateButton onClick={() => setUpsertModalProps({ groupId: undefined, open: true })} />
          </div>
        </div>

        <Table<ReorderUserRuleGroup>
          loading={reorderUserRulesLoading}
          items={items}
          onSelected={checked => setSelected(checked)}
          headers={getHeaders(productMasterData, stockLocations)}
          onClick={clicked => setUpsertModalProps({ groupId: clicked.groupId, open: true })}
          uniqueIdentifier='groupId'
          filter={filter}
        />
      </div>
    </>
  );
}
